import type { ReactNode } from 'react';
import { Calendar, Shield, Users, MessageSquare, AlertCircle, Info, Landmark } from 'lucide-react';
import type { NewsCategory } from '../../types';

export type CategoryTone = 'primary' | 'warning' | 'info' | 'danger' | 'neutral';

export interface CategoryInfo {
    id: NewsCategory;
    label: string;
    /** Short label for tabs / badges. */
    short: string;
    tone: CategoryTone;
    icon: ReactNode;
}

export const NEWS_CATEGORIES: CategoryInfo[] = [
    { id: 'community', label: 'Community news', short: 'Community', tone: 'primary', icon: <MessageSquare size={18} aria-hidden="true" /> },
    { id: 'newsletter', label: 'Newsletter', short: 'Newsletters', tone: 'primary', icon: <Calendar size={18} aria-hidden="true" /> },
    { id: 'emergency', label: 'Emergency', short: 'Emergency', tone: 'danger', icon: <AlertCircle size={18} aria-hidden="true" /> },
    { id: 'security_report', label: 'Security report', short: 'Security', tone: 'warning', icon: <Shield size={18} aria-hidden="true" /> },
    { id: 'meeting_minutes', label: 'Meeting minutes', short: 'Meetings', tone: 'info', icon: <Users size={18} aria-hidden="true" /> },
    // Forest Town's own past: the 1986 Centenary newsletter and anything else
    // worth keeping that is read for its history rather than for its news.
    { id: 'heritage', label: 'From the archive', short: 'Archive', tone: 'neutral', icon: <Landmark size={18} aria-hidden="true" /> },
    { id: 'other', label: 'Other', short: 'Other', tone: 'neutral', icon: <Info size={18} aria-hidden="true" /> },
];

const FALLBACK: CategoryInfo = { id: 'other', label: 'Update', short: 'Update', tone: 'neutral', icon: <Info size={18} aria-hidden="true" /> };

export function categoryInfo(id: string): CategoryInfo {
    return NEWS_CATEGORIES.find((c) => c.id === id) ?? FALLBACK;
}

export type FileKind = 'pdf' | 'doc' | 'sheet' | 'text' | 'file';

export function fileKind(fileName: string): FileKind {
    const ext = fileName.toLowerCase().split('.').pop() ?? '';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'doc' || ext === 'docx' || ext === 'odt' || ext === 'rtf') return 'doc';
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv' || ext === 'ods') return 'sheet';
    if (ext === 'txt' || ext === 'md') return 'text';
    return 'file';
}

export function fileKindLabel(fileName: string): string {
    switch (fileKind(fileName)) {
        case 'pdf': return 'PDF';
        case 'doc': return 'Word';
        case 'sheet': return 'Spreadsheet';
        case 'text': return 'Text';
        default: return (fileName.split('.').pop() ?? 'File').toUpperCase();
    }
}

export function formatBytes(bytes?: number | null): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
